import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';

import { readRefusal } from '@/api/api';

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

const GROUP_NAME_REQUIRED_MESSAGE: TranslationKey = 'competencies.validation.groupNameRequired';
const COMPETENCY_NAME_REQUIRED_MESSAGE: TranslationKey =
    'competencies.validation.competencyNameRequired';
const COMPETENCY_GROUP_REQUIRED_MESSAGE: TranslationKey =
    'competencies.validation.competencyGroupRequired';
const NAME_TOO_LONG_MESSAGE: TranslationKey = 'competencies.validation.nameTooLong';
const DESCRIPTION_TOO_LONG_MESSAGE: TranslationKey = 'competencies.validation.descriptionTooLong';

const PROFILE_NAME_REQUIRED_MESSAGE: TranslationKey =
    'competencies.profiles.validation.nameRequired';

const CompetencyGroupFormFields = {
    name: 'name',
    description: 'description',
} as const;

const ProfileFormFields = {
    name: 'name',
    description: 'description',
} as const;

const CompetencyFormFields = {
    name: 'name',
    description: 'description',
    competencyGroupId: 'competencyGroupId',
} as const;

const competencyGroupSchema = z.object({
    [CompetencyGroupFormFields.name]: z
        .string()
        .trim()
        .min(1, { message: GROUP_NAME_REQUIRED_MESSAGE })
        .max(NAME_MAX_LENGTH, { message: NAME_TOO_LONG_MESSAGE }),
    [CompetencyGroupFormFields.description]: z
        .string()
        .trim()
        .max(DESCRIPTION_MAX_LENGTH, { message: DESCRIPTION_TOO_LONG_MESSAGE }),
});

const competencySchema = z.object({
    [CompetencyFormFields.name]: z
        .string()
        .trim()
        .min(1, { message: COMPETENCY_NAME_REQUIRED_MESSAGE })
        .max(NAME_MAX_LENGTH, { message: NAME_TOO_LONG_MESSAGE }),
    [CompetencyFormFields.description]: z
        .string()
        .trim()
        .max(DESCRIPTION_MAX_LENGTH, { message: DESCRIPTION_TOO_LONG_MESSAGE }),
    [CompetencyFormFields.competencyGroupId]: z
        .string()
        .min(1, { message: COMPETENCY_GROUP_REQUIRED_MESSAGE }),
});

const profileSchema = z.object({
    [ProfileFormFields.name]: z
        .string()
        .trim()
        .min(1, { message: PROFILE_NAME_REQUIRED_MESSAGE })
        .max(NAME_MAX_LENGTH, { message: NAME_TOO_LONG_MESSAGE }),
    [ProfileFormFields.description]: z
        .string()
        .trim()
        .max(DESCRIPTION_MAX_LENGTH, { message: DESCRIPTION_TOO_LONG_MESSAGE }),
});

type CompetencyGroupFormData = z.infer<typeof competencyGroupSchema>;
type CompetencyFormData = z.infer<typeof competencySchema>;
type ProfileFormData = z.infer<typeof profileSchema>;

/**
 * The base/source language used for name uniqueness comparison, matching the
 * backend's MultilingualText.BaseLanguageCode.
 */
const BASE_LANGUAGE_CODE = 'en';

/**
 * Compares two multilingual name values for equality, matching the backend's
 * CompetencyNameComparison.AreSame logic. Two names collide when:
 * - The base language ("en") values match (case-insensitive, trimmed), OR
 * - Any other shared language slot has matching values.
 */
const areCompetencyNamesSame = (
    left: Record<string, string> | undefined,
    right: Record<string, string> | undefined,
): boolean => {
    if (!left || !right) {
        return false;
    }

    const normalize = (value: string): string => value.trim().toLowerCase();

    const valuesMatch = (lang: string): boolean => {
        const leftValue = left[lang];
        const rightValue = right[lang];

        if (!leftValue?.trim() || !rightValue?.trim()) {
            return false;
        }

        return normalize(leftValue) === normalize(rightValue);
    };

    if (valuesMatch(BASE_LANGUAGE_CODE)) {
        return true;
    }

    for (const lang of Object.keys(left)) {
        if (lang === BASE_LANGUAGE_CODE) {
            continue;
        }

        if (valuesMatch(lang)) {
            return true;
        }
    }

    return false;
};

/**
 * Checks if a competency name would conflict with any existing competency in the
 * target group. Used for pre-move validation to provide immediate feedback before
 * the server request.
 */
const wouldNameConflictInGroup = (
    competencyName: Record<string, string>,
    targetGroupCompetencies: Array<{ name: Record<string, string> }>,
): boolean => {
    return targetGroupCompetencies.some((existing) =>
        areCompetencyNamesSame(competencyName, existing.name),
    );
};

const hasDuplicateCompetencyNameInGroup = (
    competencies: Array<{ name: Record<string, string> }>,
): boolean => {
    for (let index = 0; index < competencies.length; index += 1) {
        for (let otherIndex = index + 1; otherIndex < competencies.length; otherIndex += 1) {
            if (areCompetencyNamesSame(competencies[index].name, competencies[otherIndex].name)) {
                return true;
            }
        }
    }

    return false;
};

/**
 * Detects whether a mutation error denotes a duplicate-name conflict rejected by
 * the server (group name unique per organization / competency name unique within
 * its group). The server surfaces this either as an HTTP 409 or as a
 * ValidationProblem whose error payload references the `name` field or a
 * conflict/duplicate code. Detection is intentionally defensive so it survives
 * small differences in the backend response shape.
 */
const isNameConflictError = (error: unknown): boolean => {
    const refusal = readRefusal(error);

    if (!refusal) {
        return false;
    }

    if (refusal.status === 409) {
        return true;
    }

    const haystacks = [
        ...Object.keys(refusal.fieldErrors),
        ...[refusal.reason, refusal.code, refusal.title].filter((value) => value !== undefined),
    ];

    return haystacks.some((value) => {
        const normalized = value.toLowerCase();

        return (
            normalized.includes('name') ||
            normalized.includes('conflict') ||
            normalized.includes('duplicate') ||
            normalized.includes('alreadyexists') ||
            normalized.includes('already_exists') ||
            normalized.includes('unique')
        );
    });
};

export {
    CompetencyGroupFormFields,
    CompetencyFormFields,
    ProfileFormFields,
    competencyGroupSchema,
    competencySchema,
    profileSchema,
    isNameConflictError,
    wouldNameConflictInGroup,
    hasDuplicateCompetencyNameInGroup,
    NAME_MAX_LENGTH,
    DESCRIPTION_MAX_LENGTH,
};
export type { CompetencyGroupFormData, CompetencyFormData, ProfileFormData };
